import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CONTRACT_API_MAP, QUANTITY_CALCULATION_TYPES } from '../constants';
import { ContractContext } from '@nile/client_management/src/Contracts/Contexts/ContractContext';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  advancedSearchConstraints,
  CustomMenu,
  CustomModal,
  AccessControl,
  DATE_FORMAT,
  EventEmitter,
  getParsedCrudData,
  getTranslatedText,
  Loader,
  reactSelectInlineError,
  SuperPop,
  validateForm,
  toast,
  useSiteListForDropdown,
  Row
} from '@nile/components';
import { MinimalDropDown } from './MinimalDropDown';
import ruleBuilder from '@nile/components/src/Validators/utils/RuleBuilder.js';
import {
  BillableContract,
  Contract,
  FrequencyDurationUnit,
  ProvisionRule
} from '@nile/types';
import {
  Badge,
  Banner,
  Button,
  ButtonGroup,
  Checkbox,
  DateTimePicker,
  DisplayText,
  TextField,
  PositiveNumberField,
  Heading
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import {
  AccountClient,
  GET_CONTRACT_PRORATED_AMOUNT,
  UPDATE_CLIENT_CONTRACT_NEW
} from '@nile/graphql';
import dayjs, { Dayjs, OpUnitType } from 'dayjs';
import { SVGIcon } from '@superops/super-icons';
import { ProratedAmount } from './ProratedAmount';
import { cloneDeep, isEmpty, omit, split } from 'lodash';
import styled from 'styled-components';
import { generate } from 'shortid';
import { validate } from 'validate.js';
import { ProvisionRuleBuilder } from '@nile/client_management/src/Contracts/DeliveryMap/ChildGroup/ProvisionRuleBuilder';
import {
  ENTITY_TYPE,
  INTEGRATION_KEYS
} from '@nile/client_management/src/Contracts/Constants';
import { DeliveryMapProvider } from '@nile/client_management/src/Contracts/Contexts/DeliveryMapContext';
import { Tooltip } from 'react-clone-tippy';

interface ProvisionRuleListOptions extends ProvisionRule {
  label: string;
  value: string;
}

const CustomMenuWithActions = ({ onClick }) => {
  return (allprops) => {
    const { innerRef, innerProps, isDisabled, children } = allprops;
    if (!!1) {
      return (
        <div>
          <CustomMenu
            {...allprops}
            actionComponent={
              <div className="ellipsis full-width cp" onClick={onClick}>
                <div className="d-in-f center linkText">
                  <SVGIcon name="Add" color="rgba(1, 31, 255, 1)" />
                  <div className="ml-5">
                    <FormattedMessage id="admin.policyManagement.assetGroup.create" />
                  </div>
                </div>
              </div>
            }
          />
        </div>
      );
    }
    return !isDisabled ? (
      <div ref={innerRef} {...innerProps} className={`customReactSelectMenu`}>
        {children}
      </div>
    ) : null;
  };
};

interface Condition {
  joinOperator: 'AND' | 'OR';
  operands:
    | Condition[]
    | {
        value: string;
        operator: string;
        attribute: string;
      }[];
}

function NewGroupForm({
  intl,
  onSave,
  saveLoading,
  onClose,
  client,
  template,
  provisionRuleListOptions
}) {
  const [newGroupData, setNewGroupData] = useState<{
    name: string;
    ruleEntityType: ENTITY_TYPE;
    parentRule?: {
      provisionRuleId: string;
    };
  }>({
    name: '',
    ruleEntityType: null,
    parentRule: {
      provisionRuleId: null
    }
  });

  const [ruleBuilderConditions, setRuleBuilderConditions] = useState();

  const [errors, setErrors] = useState<Record<string, any> | null>(null);

  const provisionRuleOptions = useMemo(() => {
    const ruleGroups = [];
    provisionRuleListOptions
      ?.filter((rule) => !rule?.parentRule && rule?.ruleEntityType !== 'CLIENT')
      ?.forEach((rule) => {
        const options = [];
        // if (rule?.ruleEntityType === 'CLIENT') {
        //   options.push(rule);
        // } else {
        provisionRuleListOptions
          ?.filter((rule) => rule?.ruleEntityType !== 'CLIENT')
          ?.map((childRule) => {
            if (childRule?.ruleEntityType === rule?.ruleEntityType) {
              options.push(childRule);
            }
          });
        // }

        ruleGroups?.push({
          label: rule?.label,
          options: options
        });
      });
    return ruleGroups;
  }, [provisionRuleListOptions]);

  function validate() {
    const validationResult = validateForm(
      { ...newGroupData, ruleCondition: ruleBuilderConditions },
      {
        name: {
          presence: { allowEmpty: false }
        },
        ruleEntityType: {
          presence: { allowEmpty: false }
        },
        ...(newGroupData?.ruleEntityType && {
          ruleCondition: {
            presence: { allowEmpty: false }
          }
        }),
        ...(ruleBuilderConditions?.operands?.length && {
          ...ruleBuilder('ruleCondition.operands')
        })
      }
    );
    if (validationResult) {
      setErrors(validationResult);
    }
    return validationResult;
  }

  function handleSave() {
    if (validate()) {
      return;
    }
    setErrors(null);

    onSave({
      ...newGroupData,
      ruleCondition: ruleBuilderConditions,
      parentRule: {
        provisionRuleId: newGroupData?.parentRule?.provisionRuleId
      }
    });
  }

  return (
    <DeliveryMapProvider
      value={{
        client: client,
        template: template,
        isTemplate: !!template?.templateId
      }}
    >
      <div className="white-bg br10 w-80vw height-60vh">
        <div className="fx actr p20 divider">
          <DisplayText element="h1" weight="Bold">
            <FormattedMessage id="New group" />
          </DisplayText>
          <span className="mlauto">
            <ButtonGroup>
              <Button plainSecondary onClick={onClose} disabled={saveLoading}>
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button primary onClick={handleSave} loading={saveLoading}>
                <FormattedMessage id="common.save" />
              </Button>
            </ButtonGroup>
          </span>
        </div>
        <div className="fx col g20 p20 tw-overflow-y-auto tw-h-[calc(100%-100px)]">
          <TextField
            type="text"
            size="medium"
            placeholder={getTranslatedText(
              intl,
              'msp.contracts.addNodeModal.placeholder'
            )}
            value={newGroupData?.name}
            label={getTranslatedText(intl, 'msp.contracts.new.group')}
            labelHidden
            onChange={(val) => {
              setNewGroupData((newGroupData) => ({
                ...newGroupData,
                name: val ? val : null
              }));
            }}
            autoComplete="off"
            error={
              errors?.name &&
              getTranslatedText(intl, 'validation.description.empty')
            }
          />
          <div className="fx col">
            <div className="fx actr">
              <DisplayText weight="SemiBold">
                <FormattedMessage id="Parent" />:{' '}
              </DisplayText>
              <DisplayText size="small">
                <FormattedMessage id="Every item needs hierarchy of where group belongs to" />
              </DisplayText>
            </div>
            <div className="w300">
              <MinimalDropDown
                value={provisionRuleListOptions?.find(
                  (opt) => opt?.value === newGroupData?.parentRule?.value
                )}
                placeholder={getTranslatedText(intl, 'common.choose')}
                options={provisionRuleOptions}
                onChange={(selectedOption) => {
                  if (selectedOption?.ruleEntityType) {
                    setNewGroupData((newGroupData) => {
                      return {
                        ...newGroupData,
                        ruleEntityType: selectedOption.ruleEntityType,
                        parentRule: selectedOption
                      };
                    });
                  }
                }}
              />
              {errors?.['ruleEntityType'] &&
                reactSelectInlineError(
                  getTranslatedText(intl, 'Please choose a rule')
                )}
            </div>
          </div>
          {newGroupData?.ruleEntityType && (
            <div className="">
              <ProvisionRuleBuilder
                updateCondition={setRuleBuilderConditions}
                conditions={ruleBuilderConditions}
                ruleEntityType={newGroupData?.ruleEntityType}
                errors={errors?.['ruleCondition.operands']}
              />
              {errors?.['ruleCondition'] &&
                reactSelectInlineError(
                  getTranslatedText(intl, 'Provision rule cannot be empty')
                )}
            </div>
          )}
        </div>
      </div>
    </DeliveryMapProvider>
  );
}

const QuantitySelectionField = ({
  errors,
  startDate,
  endDate,
  contract,
  updateContract,
  billableContract,
  nextBillDate,
  nextBillDateLoading
}: {
  errors?: object;
  startDate?: string;
  endDate?: string;
  contract?: Contract;
  nextBillDate?: string;
  nextBillDateLoading?: boolean;
  updateContract?: (key, value) => void;
  billableContract?: BillableContract;
}) => {
  const intl = useIntl();

  const {
    client,
    template,
    dateFormat,
    isEditAuthorised,
    readOnly,
    contractType
  } = useContext(ContractContext);

  const QuantityCalculationType = QUANTITY_CALCULATION_TYPES.find((Qcal) => {
    return Qcal?.value === billableContract?.quantityCalculationType;
  });

  const {
    getProvisionRuleKey,
    getProvisionRuleQuery,
    createProvisionRuleKey,
    createProvisionRuleQuery
  } = CONTRACT_API_MAP[
    template?.templateId ? 'contractTemplate' : 'clientContract'
  ];

  const [provisionRuleListOptions, setProvisionRuleListOptions] = useState<
    ProvisionRuleListOptions[]
  >([]);

  const [active, setActive] = useState(false);

  const [showCreateGroupModal, setShowCreateGroupModal] = useState<boolean>(
    false
  );

  const [quantityInputField, setQuantityInputField] = useState<string>(
    billableContract?.cumulatedQuantity || ''
  );

  const [fetchProvisionRuleList, { loading }] = useLazyQuery(
    getProvisionRuleQuery,
    {
      client: AccountClient,
      fetchPolicy: 'no-cache',
      variables: {
        input: template?.templateId
          ? {
              templateId: template?.templateId
            }
          : {
              accountId: client?.accountId
            }
      },
      onCompleted: (res) => {
        if (res?.[getProvisionRuleKey]) {
          setProvisionRuleListOptions(
            res?.[getProvisionRuleKey]?.provisionRules?.map((rule) => {
              return {
                ...rule,
                label: getTranslatedText(intl, rule?.name),
                value: rule?.provisionRuleId
              };
            })
          );
        }
      }
    }
  );

  const [
    createProvisionRule,
    { loading: createProvisionRuleLoaing }
  ] = useMutation(createProvisionRuleQuery, {
    client: AccountClient,
    fetchPolicy: 'no-cache'
  });

  function createNewGroup(provisionRuleData) {
    createProvisionRule({
      variables: {
        input: {
          ...(client?.accountId
            ? { client: { accountId: client?.accountId } }
            : { template: { templateId: template?.templateId } }),
          provisionRule: provisionRuleData
        }
      }
    }).then((res) => {
      const createdGroup = res?.data?.[createProvisionRuleKey];
      if (createdGroup) {
        setProvisionRuleListOptions((provisionRuleListOptions) => {
          const ProvisionRuleListOptionsClone = cloneDeep(
            provisionRuleListOptions
          );
          ProvisionRuleListOptionsClone.push({
            ...createdGroup,
            value: createdGroup?.provisionRuleId,
            label: createdGroup?.name
          });
          return ProvisionRuleListOptionsClone;
        });

        provisionRuleSelectHandler({
          ...createdGroup,
          value: createdGroup?.provisionRuleId,
          label: createdGroup?.name
        });
      }
      setShowCreateGroupModal(false);
    });
  }

  function onChangeHandler(updatedObj) {
    if (updateContract)
      updateContract('billableContract', {
        ...billableContract,
        ...updatedObj
      });
  }

  const {
    data: allSites
  }: {
    data: any[];
  } = useSiteListForDropdown(client?.accountId);

  const provisionRuleSelectHandler = (option: ProvisionRuleListOptions) => {
    let totalQuantity =
      option?.ruleEntityType === 'CLIENT'
        ? 1
        : option?.siteQuantities?.length
        ? option?.siteQuantities
            ?.filter((siteQuantity) => {
              if (billableContract?.billableSiteType === 'INCLUDED') {
                const includedSites = billableContract?.sites?.map((site) => {
                  return site?.id;
                });
                return includedSites?.includes(siteQuantity?.site?.id);
              }
              // if (billableContract?.billableSiteType === 'HQ') {
              //   return !!siteQuantity?.site?.hq;
              // }

              return true;
            })
            ?.reduce((siteQuantityA, siteQuantityB) => {
              return siteQuantityA + (siteQuantityB?.quantity || 0);
            }, 0)
        : 0;

    onChangeHandler({
      provisionRule: {
        provisionRuleId: option?.value || option?.provisionRuleId,
        siteQuantities: option?.siteQuantities
      },
      changes: [
        {
          ...billableContract?.changes?.[0],
          quantity: `${totalQuantity}`,
          ...(billableContract?.recurringContract?.recurringMode !==
            'ARREAR' && { effectiveDate: null })
        }
      ]
    });
    setQuantityInputField(totalQuantity.toString());
  };

  useEffect(() => {
    if (billableContract?.provisionRule) {
      const selectedProvisionRule = provisionRuleListOptions?.find((rule) => {
        return (
          rule?.provisionRuleId ===
          billableContract?.provisionRule?.provisionRuleId
        );
      });
      if (selectedProvisionRule)
        provisionRuleSelectHandler(selectedProvisionRule);
    }
  }, [billableContract?.billableSiteType, billableContract?.sites]);

  useEffect(() => {
    if (billableContract?.quantityCalculationType === 'DYNAMIC') {
      fetchProvisionRuleList();
    }
  }, [billableContract?.quantityCalculationType]);

  useEffect(() => {
    if (billableContract?.cumulatedQuantity !== quantityInputField) {
      setQuantityInputField(billableContract?.cumulatedQuantity);
    }
  }, [billableContract?.cumulatedQuantity]);

  useEffect(() => {
    if (billableContract?.sellingPriceCalculationType === 'DYNAMIC') {
      setQuantityInputField('1');
    }
  }, [billableContract?.sellingPriceCalculationType]);

  useEffect(() => {
    EventEmitter.subscribe(`FetchProvisionRuleItems`, fetchProvisionRuleList);
    return () => {
      EventEmitter.unsubscribe('FetchProvisionRuleItems');
    };
  }, []);

  if (billableContract?.recurringContract?.pax8Subscription?.subscriptionId) {
    return (
      <div className="fx actr pl-15">
        <FormattedMessage id="Billed for" />
        <span className="w180p">
          <Badge size="large">
            {getTranslatedText(intl, 'msp.contracts.form.field.quantity.pax8')}
          </Badge>
        </span>
        <div className="fx actr g10 pl-15">
          <FormattedMessage id="Quantity" />
          <TextField
            min={0}
            type="number"
            value={quantityInputField}
            label="Quantity"
            labelHidden
            autoComplete="off"
            onChange={(val) => {}}
            disabled
            error={
              errors?.['changes']?.[0] && (
                <FormattedMessage id="msp.contracts.form.field.quantity.blank.error" />
              )
            }
          />
        </div>
      </div>
    );
  }

  if (billableContract?.sellingPriceCalculationType === 'DYNAMIC') {
    return null;
  }

  let showManageQuantity = false;

  if (
    (billableContract?.frequencyType === 'RECURRING' && !!readOnly) ||
    (client?.accountId &&
      billableContract?.recurringContract?.recurringMode === 'ARREAR' &&
      dayjs(startDate).isBefore(dayjs()) &&
      billableContract?.changes?.length >= 1 &&
      billableContract?.changes?.some((change) => !!change?.changeId))
  ) {
    showManageQuantity = true;
  }

  if (
    billableContract?.quantityCalculationType === 'DYNAMIC' ||
    ['BLOCK_HOUR', 'BLOCK_MONEY'].includes(
      billableContract?.chargeItem?.serviceTypeItem?.offeringType
    )
  ) {
    showManageQuantity = false;
  }

  if (billableContract?.usageContract?.integrationSource) {
    return (
      <Row className="actr pl-15" gap={5}>
        <FormattedMessage id="msp.contracts.form.field.billed.for" />
        <Badge>
          {getTranslatedText(
            intl,
            INTEGRATION_KEYS[billableContract?.usageContract?.integrationSource]
          )}
        </Badge>
        <div className="fx actr g10">
          <FormattedMessage id="common.quantity" />
          <TextField
            min={0}
            type="number"
            value={quantityInputField}
            label="Quantity"
            labelHidden
            autoComplete="off"
            disabled
            error={
              errors?.['changes']?.[0] && (
                <FormattedMessage id="msp.contracts.form.field.quantity.blank.error" />
              )
            }
          />
        </div>
      </Row>
    );
  }

  const selectedOption = provisionRuleListOptions?.find((rule) => {
    return (
      rule?.provisionRuleId === billableContract?.provisionRule?.provisionRuleId
    );
  });

  return (
    <span className="fx col g20">
      {showCreateGroupModal && (
        <CustomModal
          open={showCreateGroupModal}
          onClose={() => setShowCreateGroupModal(false)}
        >
          <NewGroupForm
            intl={intl}
            client={client}
            template={template}
            onSave={createNewGroup}
            saveLoading={createProvisionRuleLoaing}
            onClose={() => setShowCreateGroupModal(false)}
            provisionRuleListOptions={provisionRuleListOptions}
          />
        </CustomModal>
      )}
      {((!['BLOCK_MONEY'].includes(
        billableContract?.chargeItem?.serviceTypeItem?.offeringType
      ) &&
        billableContract?.quantityCalculationType === 'FIXED') ||
        billableContract?.quantityCalculationType === 'DYNAMIC') && (
        <Heading element="h4" weight="Bold">
          <FormattedMessage
            id={
              billableContract?.quantityCalculationType === 'DYNAMIC'
                ? 'msp.contracts.form.field.select.billing.group'
                : 'msp.contracts.form.field.enter.quantity'
            }
          />
        </Heading>
      )}

      {billableContract?.quantityCalculationType === 'DYNAMIC' && (
        <div>
          <Row className="actr" gap={5}>
            {/* <FormattedMessage id="msp.contracts.form.field.billed.for" /> */}
            <span className="w180p wm-400 fg1">
              {loading ? (
                <div className="w180p">
                  <Loader />
                </div>
              ) : (
                <Tooltip
                  delay={300}
                  position="bottom-start"
                  trigger="mouseenter"
                  disabled={!selectedOption?.label}
                  html={<div className="p5 br-10">{selectedOption?.label}</div>}
                >
                  <MinimalDropDown
                    name="subscriptionTypes"
                    value={selectedOption}
                    onChange={provisionRuleSelectHandler}
                    options={provisionRuleListOptions?.filter(
                      (rule) =>
                        rule?.ruleEntityType === 'CLIENT' || !!rule?.parentRule
                    )}
                    placeholder={getTranslatedText(intl, 'app.chooseValue.pl')}
                    isDisabled={!!readOnly}
                    components={{
                      Menu: CustomMenuWithActions({
                        onClick: () => setShowCreateGroupModal(true)
                      })
                    }}
                    maxMenuHeight={150}
                  />
                </Tooltip>
              )}
            </span>
          </Row>
          {errors?.provisionRule &&
            reactSelectInlineError(
              getTranslatedText(
                intl,
                'msp.contracts.form.field.billed.for.empty.error'
              )
            )}
        </div>
      )}
      {!['BLOCK_MONEY'].includes(
        billableContract?.chargeItem?.serviceTypeItem?.offeringType
      ) &&
        billableContract?.quantityCalculationType === 'FIXED' && (
          <div className="fx actr g10">
            {/* <FormattedMessage id="common.quantity" /> */}
            <TextField
              min={0}
              type="number"
              value={quantityInputField}
              label="Quantity"
              labelHidden
              autoComplete="off"
              suffix={getTranslatedText(intl, 'app.field.suffix.qty')}
              onChange={(val) => {
                const absoluteVal = val
                  ? ['BLOCK_HOUR'].includes(
                      billableContract?.chargeItem?.serviceTypeItem
                        ?.offeringType
                    )
                    ? Math.abs(parseFloat(val)).toString()
                    : Math.abs(parseInt(val)).toString()
                  : null;
                setQuantityInputField(absoluteVal || '');
                onChangeHandler({
                  cumulatedQuantity: absoluteVal
                    ? absoluteVal?.toString()
                    : null,
                  changes: [
                    {
                      ...billableContract?.changes?.[0],
                      quantity: absoluteVal ? absoluteVal?.toString() : null,
                      quantityChangeOperation: 'BASELINE'
                    }
                  ]
                });
              }}
              onBlur={() => {
                let absoluteVal = quantityInputField;

                if (absoluteVal.split('.')?.[1]) {
                  absoluteVal = Math.abs(parseFloat(absoluteVal))?.toFixed(2);
                }

                setQuantityInputField(absoluteVal || '');
                onChangeHandler({
                  cumulatedQuantity: absoluteVal
                    ? absoluteVal?.toString()
                    : null,
                  changes: [
                    {
                      ...billableContract?.changes?.[0],
                      quantity: absoluteVal ? absoluteVal?.toString() : null,
                      quantityChangeOperation: 'BASELINE'
                    }
                  ]
                });
              }}
              disabled={
                (client?.accountId &&
                  billableContract?.recurringContract?.recurringMode ===
                    'ARREAR' &&
                  dayjs(startDate).isBefore(dayjs()) &&
                  billableContract?.changes?.length >= 1 &&
                  billableContract?.changes?.some(
                    (change) => !!change?.changeId
                  )) ||
                !!readOnly
              }
              error={
                errors?.['changes']?.[0] && (
                  <FormattedMessage id="msp.contracts.form.field.quantity.blank.error" />
                )
              }
            />
          </div>
        )}
      {/* Arun - to do check if changes > 1 or form is in edit mode and display below */}
      {showManageQuantity && (
        <AccessControl
          roleToCheck="contract.edit"
          render={
            <Tippy
              content={
                active ? (
                  <ManageChangeComponent
                    intl={intl}
                    changes={billableContract?.changes}
                    contract={contract}
                    maxChangeDecrease={billableContract?.cumulatedQuantity}
                    billableContract={billableContract}
                    startDate={startDate}
                    endDate={endDate}
                    client={client}
                    dateFormat={dateFormat}
                    isEditAuthorised={isEditAuthorised}
                    close={() => setActive(false)}
                    nextBillDate={nextBillDate}
                    nextBillDateLoading={nextBillDateLoading}
                  />
                ) : null
              }
              duration={250}
              delay={500}
              interactive
              placement="bottom-end"
              allowHTML
              theme="light"
              trigger="manual"
              zIndex={600}
              visible={active}
              maxWidth={600}
            >
              <span
                onClick={() => setActive(!active)}
                className="linkText w-fit"
              >
                {getTranslatedText(
                  intl,
                  'msp.contracts.form.field.quantity.manage'
                )}
              </span>
            </Tippy>
          }
        />
      )}
    </span>
  );
};

export default QuantitySelectionField;

export function ManageChangeComponent({
  changes: changesFromProps,
  contract,
  close,
  onSuccess,
  ...rest
}) {
  const [changes, setChanges] = useState(changesFromProps || []);

  const intl = useIntl();

  function addNewChange() {
    const clonedChanges = cloneDeep(changes);
    clonedChanges.push({
      changeId: generate(),
      quantity: null,
      quantityChangeOperation: 'INCREASE',
      effectiveDate: null,
      generateInvoice: false,
      isNewChange: true
    });
    setChanges(clonedChanges);
  }

  function removeChange(changeId) {
    const clonedChanges = cloneDeep(changes);
    const updatedChanges = clonedChanges.filter(
      (change) => change?.changeId !== changeId
    );
    setChanges(updatedChanges);
  }

  function updateChangeHandler(changeId) {
    return (newChangeObj) => {
      const clonedChanges = cloneDeep(changes);
      const updatedChanges = clonedChanges.map((change) => {
        if (change?.changeId === changeId) {
          return newChangeObj;
        }
        return change;
      });
      setChanges(updatedChanges);
    };
  }

  const [loading, setLoading] = useState<boolean>(false);

  function confirmChangeHanlder() {
    const { addedItems } = getParsedCrudData(
      changesFromProps,
      changes,
      'changeId'
    );
    setLoading(true);
    AccountClient.mutate({
      mutation: UPDATE_CLIENT_CONTRACT_NEW,
      variables: {
        input: {
          contract: {
            contractId: contract?.contractId,
            billableContract: {
              change: omit(addedItems?.[0], ['changeId', 'isNewChange'])
            }
          }
        }
      }
    })
      .then((res) => {
        close();
        setLoading(false);
        toast('success', 'client.contract.update.success');
        EventEmitter.dispatch('refetchContract');
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setLoading(false);
      });
  }

  return (
    <div className="min-width-600 p10 g10 fx col astart">
      <div className="fw fx actr p5 lightpurple-to-black-bg">
        <span className="fg1 mw-40p">
          <DisplayText>
            <FormattedMessage id="msp.contracts.form.field.manage.quantity.updated.qty" />
          </DisplayText>
        </span>
        <span className="fx actr g10">
          |
          <DisplayText>
            <FormattedMessage id="msp.contracts.form.field.manage.quantity.effective.from" />
          </DisplayText>
        </span>
      </div>
      {changes?.map((change) => {
        return (
          <Change
            {...rest}
            change={change}
            loading={loading}
            contract={contract}
            removeChange={removeChange}
            confirmChange={confirmChangeHanlder}
            updateChange={updateChangeHandler(change?.changeId)}
          />
        );
      })}
      {(!changes?.length || changes?.length === changesFromProps?.length) && (
        <AccessControl
          roleToCheck="contract.edit"
          render={
            <Button
              plain
              onClick={addNewChange}
              icon={<SVGIcon name="Add" />}
              children={getTranslatedText(
                intl,
                'msp.contracts.form.field.manage.quantity.add.new'
              )}
            />
          }
        />
      )}
    </div>
  );
}

const Change = (props) => {
  const {
    intl,
    startDate,
    endDate,
    client,
    loading,
    contract,
    billableContract,
    dateFormat,
    isEditAuthorised,
    change,
    updateChange,
    confirmChange,
    removeChange,
    nextBillDate,
    nextBillDateLoading,
    maxChangeDecrease
  } = props;
  const [proratedAmount, setProratedAmount] = useState();

  const [errors, setErrors] = useState<{ [x: string]: any } | null>(null);

  const {
    sellingPrice,
    recurringContract,
    frequencyType
  }: BillableContract = billableContract;

  const [
    getProratedAmount,
    { loading: proratedAmountLoading, data, error }
  ] = useLazyQuery(GET_CONTRACT_PRORATED_AMOUNT, {
    client: AccountClient,
    onCompleted(response) {
      if (response) {
        setProratedAmount(response?.getContractProRatedAmount);
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  function onChangeHandler(key, value) {
    updateChange({ ...change, [key]: value });
  }

  function onConfirm() {
    const isInvalid = validate(change, {
      quantity: { presence: true }
    });
    if (isInvalid) {
      setErrors(isInvalid);
      return;
    }
    setErrors(null);
    confirmChange();
  }

  const getProratedAmountFunction = (quantity, effectiveDate) => {
    if (
      !isEmpty(quantity) &&
      startDate &&
      recurringContract &&
      frequencyType &&
      effectiveDate &&
      change?.quantityChangeOperation === 'INCREASE'
    ) {
      getProratedAmount({
        variables: {
          input: {
            contract: contract?.contractId
              ? {
                  contractId: contract?.contractId
                }
              : null,
            itemType: billableContract?.chargeItem?.itemType,
            offeringType:
              billableContract?.chargeItem?.serviceTypeItem?.offeringType,
            billDate: startDate,
            effectiveDate: dayjs(effectiveDate).format(DATE_FORMAT),
            endDate: endDate || undefined,
            unitPrice: billableContract?.sellingPrice,
            quantity: quantity ? quantity : undefined,
            frequency: {
              frequencyDurationUnit: recurringContract?.frequencyDurationUnit,
              frequencyInterval: recurringContract?.frequencyInterval,
              recurringMode: recurringContract?.recurringMode,
              billingOffset:
                recurringContract?.recurringMode === 'ADVANCE'
                  ? recurringContract?.billingOffset
                  : null
            }
          }
        }
      });
    }
  };

  let nextBillDateBasedOnBillingType: Dayjs | string = dayjs(
    nextBillDate
  ).format('YYYY-MM-DD');

  if (
    nextBillDateBasedOnBillingType &&
    recurringContract?.billingOffset &&
    recurringContract?.recurringMode === 'ADVANCE'
  ) {
    nextBillDateBasedOnBillingType = dayjs(nextBillDate)
      .add(recurringContract?.billingOffset, 'days')
      .format('YYYY-MM-DD');
  }
  // const DurationUnitsMap: Record<FrequencyDurationUnit, OpUnitType> = {
  //   MONTH: 'month',
  //   DAY: 'day',
  //   WEEK: 'week',
  //   YEAR: 'year'
  // };
  // if (
  //   nextBillDateBasedOnBillingType &&
  //   recurringContract?.recurringMode === 'ARREAR' &&
  //   recurringContract?.frequencyInterval
  // ) {
  //   nextBillDateBasedOnBillingType = dayjs(nextBillDate)
  //     .subtract(
  //       recurringContract?.frequencyInterval,
  //       DurationUnitsMap[recurringContract?.frequencyDurationUnit]
  //     )
  //     .format('YYYY-MM-DD');
  // }

  const dateTypeMap: Record<FrequencyDurationUnit, OpUnitType> = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year'
  };

  const dateType: OpUnitType =
    dateTypeMap[recurringContract?.frequencyDurationUnit || 'DAY'];

  const getNextBillingCycle = (startDate) => {
    const currentDate = dayjs(dayjs().toDate()).format('YYYY-MM-DD');

    const updatedStartDate = dayjs(startDate)
      .add(recurringContract?.frequencyInterval || 1, dateType)
      .toDate();
    const updatedEnddDate = dayjs(updatedStartDate)
      .add(recurringContract?.frequencyInterval || 1, dateType)
      .subtract(1, 'day')
      .toDate();

    if (currentDate <= dayjs(updatedEnddDate).format('YYYY-MM-DD')) {
      return {
        start: updatedStartDate,
        end: updatedEnddDate
      };
    }

    return getNextBillingCycle(updatedStartDate);
  };

  const getDateRange = (eDate) => {
    const currentMonth = dayjs().get('month') + 1;
    const currentYear = dayjs().get('year');
    const currentDay = dayjs().get('date');
    const effectiveDateArr = eDate.split('-');
    const effectiveDateMonth = effectiveDateArr[1];
    const effectiveDateYear = effectiveDateArr[0];
    const effectiveDateDay = effectiveDateArr[2];
    const currentDate = dayjs(dayjs().toDate()).format('YYYY-MM-DD');

    if (
      recurringContract?.recurringMode !== 'ARREAR' &&
      eDate === currentDate
    ) {
      const startDate = dayjs(eDate).toDate();
      const enddDate = dayjs(startDate)
        .add(recurringContract?.frequencyInterval || 1, dateType)
        .subtract(1, 'day')
        .toDate();

      if (currentDate <= dayjs(enddDate).format('YYYY-MM-DD')) {
        return {
          start: startDate,
          end: enddDate
        };
      }

      return getNextBillingCycle(startDate);
    }

    return {
      start: dayjs(
        `${effectiveDateYear}-${effectiveDateMonth}-${effectiveDateDay}`
      )
        .subtract(recurringContract?.frequencyInterval || 1, dateType)
        .toDate(),
      end: dayjs(
        `${effectiveDateYear}-${effectiveDateMonth}-${effectiveDateDay}`
      )
        .subtract(1, 'day')
        .toDate()
    };
  };

  const getMaxEffectiveDate = () => {
    let maxEffectiveDate: Date | null = null;

    if (nextBillDateBasedOnBillingType) {
      maxEffectiveDate = getDateRange(nextBillDateBasedOnBillingType).end;
    }

    if (
      endDate &&
      nextBillDateBasedOnBillingType &&
      dayjs(getDateRange(endDate).end).isBefore(nextBillDateBasedOnBillingType)
    ) {
      maxEffectiveDate = dayjs(endDate).toDate();
    }

    return maxEffectiveDate;
  };

  return (
    <div>
      <span className="fw fx actr g20">
        <div className="mw-50p ellipsis">
          <PositiveNumberField
            min={0}
            labelHidden
            type="number"
            label="Quantity"
            size="small"
            autoComplete="off"
            value={change?.quantity}
            error={errors?.quantity?.[0]}
            max={
              change?.quantityChangeOperation === 'DECREASE' &&
              maxChangeDecrease
            }
            onChange={(val) => {
              onChangeHandler('quantity', val?.toString());
              getProratedAmountFunction(
                val,
                dayjs(change?.effectiveDate).format(DATE_FORMAT)
              );
            }}
            disabled={!change?.isNewChange}
            suffix={
              <OperationHolder
                className={`fx actr ${
                  !change?.isNewChange ? 'cursor-not-allowed' : ''
                }`}
              >
                <Operation
                  selected={change?.quantityChangeOperation !== 'DECREASE'}
                  onClick={() => {
                    if (change?.isNewChange) {
                      updateChange({
                        ...change,
                        quantity: null,
                        ['quantityChangeOperation']: 'INCREASE',
                        effectiveDate: null
                      });
                    }
                  }}
                >
                  <SVGIcon
                    name="ArrowDown"
                    color={
                      change?.quantityChangeOperation !== 'DECREASE'
                        ? 'white'
                        : 'black'
                    }
                  />
                </Operation>
                <Operation
                  selected={change?.quantityChangeOperation === 'DECREASE'}
                  type="DECREASE"
                  onClick={() => {
                    if (change?.isNewChange) {
                      let effectiveDate = null;

                      if (
                        nextBillDate &&
                        recurringContract?.recurringMode === 'UPFRONT'
                      ) {
                        effectiveDate = dayjs(nextBillDate)
                          .subtract(1, 'day')
                          .format(DATE_FORMAT);
                      }

                      if (
                        nextBillDate &&
                        recurringContract?.recurringMode === 'ADVANCE'
                      ) {
                        const dateTypeMap: Record<string, OpUnitType> = {
                          DAY: 'day',
                          WEEK: 'week',
                          MONTH: 'month',
                          YEAR: 'year'
                        };
                        effectiveDate = dayjs(nextBillDate)
                          .add(recurringContract?.billingOffset, 'days')
                          .subtract(1, 'day')
                          .format(DATE_FORMAT);
                      }

                      updateChange({
                        ...change,
                        quantity: null,
                        ['quantityChangeOperation']: 'DECREASE',
                        effectiveDate: effectiveDate
                      });
                    }
                  }}
                >
                  <SVGIcon
                    name="ArrowDown"
                    color={
                      change?.quantityChangeOperation === 'DECREASE'
                        ? 'white'
                        : 'black'
                    }
                  />
                </Operation>
              </OperationHolder>
            }
          />
        </div>
        <div className="fx actr g10 mw-30 ellipsis">
          <SVGIcon name="DatePicker" size="sm" />
          <div className="cw-20 ellipsis">
            <DateTimePicker
              selected={
                change?.effectiveDate && dayjs(change.effectiveDate).toDate()
              }
              minDate={
                nextBillDateBasedOnBillingType
                  ? getDateRange(nextBillDateBasedOnBillingType).start
                  : null
              }
              maxDate={getMaxEffectiveDate()}
              onChange={(date) => {
                if (date) {
                  const formattedDate = dayjs(date).format(DATE_FORMAT);
                  onChangeHandler('effectiveDate', formattedDate);
                  getProratedAmountFunction(change?.quantity, formattedDate);
                } else {
                  onChangeHandler('effectiveDate', null);
                }
              }}
              placeholderText={getTranslatedText(intl, 'common.chooseDate')}
              dateFormat={dateFormat}
              title={getTranslatedText(intl, 'client.details.contract.enddate')}
              required
              showYearDropdown
              showMonthDropdown
              disabled={
                !change?.isNewChange ||
                !isEditAuthorised ||
                (recurringContract?.recurringMode !== 'ARREAR' &&
                  change?.quantityChangeOperation === 'DECREASE')
              }
              portalId="root-portal"
            />
            {errors?.effectiveDate &&
              reactSelectInlineError(
                getTranslatedText(intl, 'app.common.choose.date')
              )}
          </div>
        </div>
        {change?.isNewChange && (
          <div className="mw-30">
            <ButtonGroup spacing="tight">
              <Button
                onClick={onConfirm}
                loading={loading}
                icon={
                  <SVGIcon
                    size="sm"
                    name="CircleTickThinOutline"
                    color="Green"
                  />
                }
              />
              <Button
                disabled={!!loading}
                onClick={() => removeChange(change?.changeId)}
                icon={<SVGIcon size="sm" name="Close" color="Red" />}
              />
            </ButtonGroup>
          </div>
        )}
      </span>
      {change?.quantityChangeOperation !== 'DECREASE' &&
        change?.quantity &&
        change?.effectiveDate &&
        proratedAmount && (
          <Banner status="info" hideIcon>
            <div className="fx actr g20">
              <DisplayText size="medium">
                <FormattedMessage id="client.contracts.item.proratedAmount" />
              </DisplayText>

              <ProratedAmount
                amount={proratedAmount}
                loading={proratedAmountLoading}
              />

              <div className="ml-10">
                <Checkbox
                  label={getTranslatedText(
                    intl,
                    'client.contracts.item.generateInvoice'
                  )}
                  checked={change?.generateInvoice}
                  onChange={(checked) => {
                    onChangeHandler('generateInvoice', checked);
                  }}
                />
              </div>
              <SuperPop
                position="top-start"
                content={getTranslatedText(
                  intl,
                  'client.contracts.item.generateInvoice.supertip'
                )}
              />
            </div>
          </Banner>
        )}
    </div>
  );
};

const Operation = styled.div`
  background: ${(props: { selected?: boolean; type?: 'DECREASE' }) =>
    props.selected
      ? props.type === 'DECREASE'
        ? '#EC5151'
        : '#77BB61'
      : 'transprent'};
  transform: ${(props) =>
    props.type === 'DECREASE' ? 'rotate(0deg)' : 'rotate(180deg)'};
`;

const OperationHolder = styled.div`
  border: 1px solid #eeeeee;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.102874);
  border-radius: 2px;
  display: flex;
  height: 25px;
  cursor: ${(props: { disabled?: boolean }) =>
    props.disabled ? 'not-allowed' : 'pointer'};
`;
